import AuthTypes from '../../../libs/AuthTypes/AuthTypes.js';
import dayjs from 'dayjs';
import officeProps from './officeprops.js';

/**
 * Determines file extension and runs respective function. Handles PDF and office documents
 * @param file - document to extract metadata from
 * @returns array of objects if metadata found, otherwise empty array
 */
export default async function getMetadata(file) {
	// office props lib supports these and so does sharpr:
	const supportedOfficeExtensions = [
		'docx',
		'dotx',
		'docm',
		'xlsx',
		'xlsm',
		'xlsb',
		'xltx',
		'pptx',
		'ppsx',
		'pptm',
		'potx',
		'ods',
		'odt',
		'odp',
	];
	if (file.extension === 'pdf') {
		const pdfProperties = await extractPdfProperties(file);
		return pdfProperties;
	} else if (supportedOfficeExtensions.includes(file.extension)) {
		const officeProperties = await extractOfficeProperties(file);
		return officeProperties;
	} else {
		return [];
	}
}

/**
 * Uses FileReader to read file binary. Then we parse it and strip any metadata tags we find within and try to extract metadata properties from it
 * @param file
 * @returns array of objects if metadata is found, otherwise empty array
 */
async function extractPdfProperties(file) {
	try {
		const reader = new FileReader();
		reader.readAsText(file._nativeFile);
		// In order to get access to values within the reader.onLoad, we must wrap in promise
		const fileContents = await new Promise((resolve, reject) => {
			reader.onerror = () => {
				reader.abort();
				reject(new DOMException('Error reading file'));
			};
			reader.onload = evt => {
				const xml = evt?.target?.result.match(/<x:xmpmeta[\s\S]+<\/x:xmpmeta>/);
				if (!xml) {
					resolve([]);
				} else {
					const parser = new DOMParser();
					const doc = parser.parseFromString(xml[0], 'text/xml');
					resolve(doc);
				}
			};
		});
		const metadata = await getPropertyData(fileContents);
		return metadata;
	} catch (error) {
		return [];
	}
}

/**
 * Takes the parsed metadata xml tags and maps them to valid sharpr post data
 * @param fileContent
 * @returns array of objects if metadata is found, otherwise empty array
 */
async function getPropertyData(fileContent) {
	try {
		const pdfMetadata = await findElementMatch(fileContent);
		return filterMetadata(pdfMetadata);
	} catch (e) {
		return [];
	}
}

/**
 * Loops through the tags we need to find and extract data from the metadata xml, each tag has it's own function to handle extraction
 * @param fileContent
 * @returns array of objects if metadata was found, otherwise empty array
 */
async function findElementMatch(fileContent) {
	try {
		const elementsNeeded = {
			'dc:title': extractHeadline,
			'dc:description': extractInsights,
			'pdf:Keywords': extractKeywords,
			'xmp:CreateDate': extractCreateDate,
			'dc:creator': extractCreator,
		};
		let data = [];
		for (let el of Object.keys(elementsNeeded)) {
			const elements = Array.from(fileContent.getElementsByTagName(el));
			const elementData = await elementsNeeded[el]([...elements]);
			if (elementData) {
				data = [...data, ...elementData];
			}
		}
		return data;
	} catch (e) {
		return [];
	}
}

/**
 * Extracts title from provided element for post headline
 * @param elements - element to find within metadata xml ex: 'dc:title'
 * @returns array of objects
 */
async function extractHeadline(elements) {
	try {
		const val = elements[elements.length - 1]?.childNodes?.[1]?.childNodes?.[1]?.childNodes?.[0]?.data || null;
		return Promise.resolve([
			{
				id: 'headline',
				prop: 'Headline: ',
				value: val,
			},
		]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Extracts subject from provided element for post insight text
 * @param elements - element to find within metadata xml ex: 'dc:title'
 * @returns array of objects
 */
async function extractInsights(elements) {
	try {
		const val = elements[elements.length - 1]?.childNodes?.[1]?.childNodes?.[1]?.childNodes?.[0]?.data || null;
		return Promise.resolve([
			{
				id: 'intel_text',
				prop: 'Insight: ',
				value: val,
			},
		]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Extracts keywords from provided element for post tags
 * @param elements - element to find within metadata xml ex: 'dc:title'
 * @returns array of objects
 */
async function extractKeywords(elements) {
	try {
		const keywords = elements[elements.length - 1]?.textContent || null;
		return Promise.resolve([
			{
				id: 'tags',
				prop: 'Tags: ',
				value: keywords ? keywords.split(/[,;]+/).join(', ') : keywords,
			},
		]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Extracts created date from provided element for post source date and published_at date
 * @param elements - element to find within metadata xml ex: 'dc:title'
 * @returns array of objects
 */
async function extractCreateDate(elements) {
	try {
		const dateData = elements[elements.length - 1]?.textContent || null;
		const sourceDate = {
			id: 'source_date',
			prop: 'Source Date: ',
			value: `${dayjs(dateData)}`,
		};
		const publishedAt = {
			id: 'published_at',
			prop: 'Published At: ',
			value: `${dayjs(dateData)}`,
		};
		return Promise.resolve([sourceDate, publishedAt]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Extract author data from provided element for post source author and submitted/published by
 * @param elements - element to find within metadata xml ex: 'dc:title'
 * @returns array of objects
 */
async function extractCreator(elements) {
	try {
		const author = elements[elements.length - 1]?.childNodes?.[1]?.childNodes?.[1]?.childNodes?.[0]?.data || null;
		const authorData = await fetchSubmitterIdByName(author);
		const sourceAuthor = {
			id: 'source_author',
			prop: 'Source Author: ',
			value: authorData.$label,
		};
		const submittedBy = {
			id: 'submitted_by',
			prop: 'Submitted by User: ',
			value: authorData.id,
			label: authorData.$label,
		};
		const publishedBy = {
			id: 'published_by',
			prop: 'Published by User: ',
			value: authorData.id,
			label: authorData.$label,
		};
		return Promise.resolve([sourceAuthor, submittedBy, publishedBy]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Takes in the metadata author and hits our API to see if we have a valid sharpr user for more user data
 * @param author - string ex: "John Doe"
 * @returns Object containing user id and label (ex: "John Doe")
 */
async function fetchSubmitterIdByName(author) {
	try {
		if (!author || !isAuthorValid(author)) {
			return {
				id: 0,
				$label: '',
			};
		} else {
			const authHeader = AuthTypes.getHeader();
			const url = `${window.BASE_URL}/api/v2/users/by_name?name=${author}`;
			const response = await fetch(url, {
				headers: { Authorization: authHeader },
			});
			const submitterData = await response.json();
			return submitterData;
		}
	} catch (e) {
		return {
			id: 0,
			$label: '',
		};
	}
}

/**
 * Tries to get metadata properties from office documents and filters out any object properties that are falsey
 * @param file
 * @returns array of objects or empty array of no metadata found or error occurs
 */
async function extractOfficeProperties(file) {
	try {
		const officeMetadata = await findOfficeProperties(file._nativeFile);
		return filterMetadata(officeMetadata);
	} catch (e) {
		return [];
	}
}

/**
 * Logic to loop through the needed properties and run their respective function.
 * @param nativeFile - the file binary content
 * @returns array of objects or empty array if error/no metadata found
 */
async function findOfficeProperties(nativeFile) {
	try {
		const properties = await officeProps.getData(nativeFile);
		const propsToFind = {
			headline: extractOfficeHeadline,
			intel_text: extractOfficeInsight,
			tags: extractOfficeKeywords,
			created_date: extractOfficeSourceDate,
			author: extractOfficeAuthor,
		};
		let officeMetadata = [];
		for (let prop of Object.keys(propsToFind)) {
			const data = await propsToFind[prop](properties);
			if (data) {
				officeMetadata = [...officeMetadata, ...data];
			}
		}
		return officeMetadata;
	} catch (e) {
		return [];
	}
}

/**
 * Extracts title from office metadata for post headline
 * @param properties - ex: 'headline'
 * @returns array of objects with headline data
 */
async function extractOfficeHeadline(properties) {
	try {
		const headline = properties?.editable?.title?.value || null;
		return Promise.resolve([
			{
				id: 'headline',
				prop: 'Headline: ',
				value: headline,
			},
		]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Extracts subject from office metadata for post insight text
 * @param properties - metadata object extracted from native file via officeprops library
 * @returns - array of objects with insight text data
 */
async function extractOfficeInsight(properties) {
	try {
		const insight = properties?.editable?.subject?.value || null;
		return Promise.resolve([
			{
				id: 'intel_text',
				prop: 'Insight: ',
				value: insight,
			},
		]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Extracts keywords from office metadata for post tags
 * @param properties - metadata object extracted from native file via officeprops library
 * @returns array of objects with keyword data
 */
async function extractOfficeKeywords(properties) {
	try {
		const keywords = properties?.editable?.keywords?.value || null;
		return Promise.resolve([
			{
				id: 'tags',
				prop: 'Tags: ',
				value: keywords ? keywords.split(/[,;]+/).join(', ') : keywords,
			},
		]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Extracts source date from office metadata for post source date and published at
 * @param properties - metadata object extracted from native file via officeprops library
 * @returns array of objects with created date data
 */
async function extractOfficeSourceDate(properties) {
	try {
		// Adds date if it exists, otherwise it'll be null and filtered out later down the line
		const createdDate = properties?.editable?.created?.value || null;
		const sourceDate = {
			id: 'source_date',
			prop: 'Source Date: ',
			value: createdDate ? `${dayjs(createdDate)}` : createdDate,
		};
		const publishedAt = {
			id: 'published_at',
			prop: 'Published At: ',
			value: createdDate ? `${dayjs(createdDate)}` : createdDate,
		};
		return Promise.resolve([sourceDate, publishedAt]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Extracts author from office doc and calls function to fetch user data for post source author, submitted and published by
 * @param properties - metadata object extracted from native file via officeprops library
 * @returns array of objects with author data
 */
async function extractOfficeAuthor(properties) {
	try {
		const author = properties?.editable?.creator?.value || null;
		const authorData = await fetchSubmitterIdByName(author);
		const sourceAuthor = {
			id: 'source_author',
			prop: 'Source Author: ',
			value: authorData.$label,
		};
		const submittedBy = {
			id: 'submitted_by',
			prop: 'Submitted by User: ',
			value: authorData.id,
			label: authorData.$label,
		};
		const publishedBy = {
			id: 'published_by',
			prop: 'Published by User: ',
			value: authorData.id,
			label: authorData.$label,
		};
		return Promise.resolve([sourceAuthor, submittedBy, publishedBy]);
	} catch (e) {
		return Promise.resolve([{}]);
	}
}

/**
 * Checks to see if name is of type string and is not empty
 * @param author - string
 * @returns {boolean}
 */
function isAuthorValid(author) {
	if (author === '' || typeof author !== 'string') {
		return false;
	} else {
		return true;
	}
}

/**
 * Goal of this function is to remove any objects that has its value property set to something falsey
 * @param data - an array of objects
 * @returns [{}] - an array of objects, or empty array
 */
function filterMetadata(data) {
	let dataToFilter = [...data];
	return dataToFilter.filter(obj => obj.value);
}
